import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useNavigate } from "react-router-dom";
import { takedownApi } from "core/api";
import { TakedownPracticeRow } from "core/types/takedown.types";
import AutoPolicyPanel from "./AutoPolicyPanel";
import RepliesInboxPage from "./RepliesInboxPage";
import EffectivenessPanel from "./EffectivenessPanel";
import ManualCheckPanel from "./ManualCheckPanel";

type ChipColor =
  | "default"
  | "primary"
  | "secondary"
  | "info"
  | "success"
  | "warning"
  | "error";

const DERIVED_STATUS_KEY: Record<string, { labelKey: string; color: ChipColor }> = {
  in_attesa: { labelKey: "takedown.list.status.in_attesa", color: "default" },
  in_gestione: { labelKey: "takedown.list.status.in_gestione", color: "warning" },
  evasa: { labelKey: "takedown.list.status.evasa", color: "success" },
};

const derivedMeta = (s: string | undefined, t: (key: string, opts?: Record<string, unknown>) => string) => {
  const m = s && DERIVED_STATUS_KEY[s];
  return m ? { label: t(m.labelKey), color: m.color } : { label: s || "—", color: "default" as ChipColor };
};

const clientName = (p: TakedownPracticeRow): string => {
  const u = p.user || {};
  const full = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return full || u.email || "—";
};

const fmtDate = (raw?: string): string => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("it-IT", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const tsOf = (raw?: string): number => {
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return isNaN(t) ? 0 : t;
};

/** Lista di TUTTE le pratiche takedown di tutti i clienti. Click → dettaglio pratica. */
export const TakedownListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<TakedownPracticeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showAll, setShowAll] = useState(false);
  const LATEST = 25;

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await takedownApi.listPractices();
        if (active) setRows(Array.isArray(data) ? data : []);
      } catch (e: any) {
        if (active) setError(t("takedown.list.error_load", { error: e?.message || e }));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q
      ? rows.filter((p) => {
          const u = p.user || {};
          const hay = [u.firstName, u.lastName, u.email, clientName(p)]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return hay.indexOf(q) !== -1;
        })
      : rows.slice();
    // filtro per stato derivato
    if (statusFilter) {
      list = list.filter(
        (p) => p.takedown_summary?.derived_status === statusFilter
      );
    }
    // ordinamento per data (created_at) desc
    return list.sort((a, b) => tsOf(b.created_at) - tsOf(a.created_at));
  }, [rows, query, statusFilter]);

  // Mostra di default solo le ULTIME pratiche (non tutte dall'inizio). Ricerca/filtro/«Mostra tutte» le espandono.
  const capped =
    !showAll && !query.trim() && !statusFilter && filtered.length > LATEST;
  const visible = capped ? filtered.slice(0, LATEST) : filtered;

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
      {/* Intestazione */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <GavelRoundedIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          {t("takedown.list.title")}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("takedown.list.description")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Policy globale di automazione (G1/G2) — vive sopra la lista pratiche. */}
      <AutoPolicyPanel />

      {/* Efficacia per canale: due righe di numeri, sta bene in testa. */}
      <EffectivenessPanel />

      {/* La lista pratiche viene PRIMA dell'inbox risposte: e' il punto di partenza del lavoro
          (si entra da qui nel dettaglio di un cliente), mentre l'inbox e' una vista trasversale
          di consultazione. Con l'inbox sopra bisognava scorrere una tabella lunga per arrivarci. */}
      <Paper sx={{ borderRadius: "10px", padding: "20px", mb: 2 }}>
        <Box
          paddingY={"15px"}
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          flexWrap={"wrap"}
          gap={2}
        >
          <Typography fontSize={"16px"} fontWeight={700}>
            {capped
              ? t("takedown.list.count_capped", { latest: LATEST, total: filtered.length })
              : t("takedown.list.count", { count: filtered.length })}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={statusFilter}
              onChange={(_e, v) => setStatusFilter(v ?? "")}
            >
              <ToggleButton value="">{t("takedown.list.status.all")}</ToggleButton>
              <ToggleButton value="in_attesa">{t("takedown.list.status.in_attesa")}</ToggleButton>
              <ToggleButton value="in_gestione">{t("takedown.list.status.in_gestione")}</ToggleButton>
              <ToggleButton value="evasa">{t("takedown.list.status.evasa")}</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              placeholder={t("takedown.list.search_placeholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ width: "30%", minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Box>

        <div style={{ overflowX: "auto" }}>
          {loading ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                padding: "40px 0",
              }}
            >
              <CircularProgress color="secondary" size={"80px"} />
            </div>
          ) : (
            <TableContainer
              component={Paper}
              style={{
                position: "relative",
                borderRadius: "8px",
                maxHeight: "560px",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("takedown.list.table.date")}</TableCell>
                    <TableCell>{t("takedown.list.table.client")}</TableCell>
                    <TableCell>{t("takedown.list.table.domains")}</TableCell>
                    <TableCell>{t("takedown.list.table.status")}</TableCell>
                    <TableCell>{t("takedown.list.table.link")}</TableCell>
                    <TableCell>{t("takedown.list.table.last_update")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody
                  sx={{
                    "& .MuiTableCell-root": {
                      padding: "10px",
                      fontSize: "14px",
                    },
                  }}
                >
                  {visible.map((p) => {
                    const sm = derivedMeta(p.takedown_summary?.derived_status, t);
                    const removed = p.takedown_summary?.url_removed ?? 0;
                    const total =
                      p.takedown_summary?.url_total ?? p.urls?.length ?? 0;
                    return (
                      <TableRow
                        key={p.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate("/web-rep/takedown/" + p.id)}
                      >
                        <TableCell>{fmtDate(p.created_at)}</TableCell>
                        <TableCell>{clientName(p)}</TableCell>
                        <TableCell>{`${p.takedown_summary?.domains_sent ?? 0}/${p.takedown_summary?.domains_total ?? 0}`}</TableCell>
                        <TableCell>
                          <Chip size="small" color={sm.color} label={sm.label} />
                        </TableCell>
                        <TableCell>{t("takedown.list.table.removed_count", { removed, total })}</TableCell>
                        <TableCell>{fmtDate(p.updated_at)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {!filtered.length && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography
                          color="text.secondary"
                          sx={{ py: 3, textAlign: "center" }}
                        >
                          {t("takedown.list.empty")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>

        {capped && (
          <Box sx={{ textAlign: "center", pt: 2 }}>
            <Button size="small" onClick={() => setShowAll(true)}>
              {t("takedown.list.show_all", { count: filtered.length })}
            </Button>
          </Box>
        )}
      </Paper>

      {/* URL che la sonda non puo' verificare (challenge anti-bot): coda di lavoro dell'operatore,
          compare solo quando c'e' qualcosa da guardare. */}
      <ManualCheckPanel />

      {/* Inbox globale risposte: vista trasversale, sotto la lista pratiche. */}
      <RepliesInboxPage />
    </Box>
  );
};

export default TakedownListPage;
